"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ActivityLogParams } from "@/components/profile-activity/ProfileActivityLogs";
import type { ApiProfileRepRatesState, RatingWithProfileInfoAndLevel } from "@/entities/IProfile";
import { SortDirection } from "@/entities/ISort";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { ApiProfileProxyActionType } from "@/generated/models/ApiProfileProxyActionType";
import { amIUser, formatNumberWithCommas } from "@/helpers/Helpers";
import type { Page } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
import { ProfileRatersParamsOrderBy, RateMatter } from "@/types/enums";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useMemo, useState } from "react";
import UserPageIdentityHeaderCICRate from "../identity/header/cic-rate/UserPageIdentityHeaderCICRate";
import UserPageIdentityStatementsAddButton from "../identity/statements/add/UserPageIdentityStatementsAddButton";
import UserPageIdentityStatements from "../identity/statements/UserPageIdentityStatements";
import UserPageRateWrapper from "../utils/rate/UserPageRateWrapper";
import UserCICStatus from "../utils/user-cic-status/UserCICStatus";
import UserCICTypeIcon from "../utils/user-cic-type/UserCICTypeIcon";
import TopRaterAvatars from "./header/TopRaterAvatars";
import UserPageRepNewRep from "./new-rep/UserPageRepNewRep";
import UserPageRepRepsTable from "./reps/table/UserPageRepRepsTable";
import UserPageCombinedActivityLog from "./UserPageCombinedActivityLog";
import { getCanEditRep, sortRepsByRatingAndContributors } from "./UserPageRep.helpers";
type MobileTab = "rep" | "identity";
export default function UserPageRepMobile(t0) {
  const $ = _c(131);
  const {
    profile,
    repRates,
    initialActivityLogParams
  } = t0;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    address
  } = useSeizeConnectContext();
  const profileHandle = profile.handle ?? "";
  const [activeTab, setActiveTab] = useState("rep");
  const [isGrantRepOpen, setIsGrantRepOpen] = useState(false);
  const [isNicRateOpen, setIsNicRateOpen] = useState(false);
  let t1;
  let t2;
  if ($[0] !== profileHandle) {
    t1 = [QueryKey.PROFILE_RATERS, {
      handleOrWallet: profileHandle,
      matter: RateMatter.NIC,
      page: 1,
      pageSize: 1,
      order: SortDirection.DESC,
      orderBy: ProfileRatersParamsOrderBy.RATING,
      given: false
    }];
    t2 = async () => await commonApiFetch({
      endpoint: `profiles/${profileHandle}/cic/ratings/by-rater`,
      params: {
        page: "1",
        page_size: "1",
        order: SortDirection.DESC.toLowerCase(),
        order_by: ProfileRatersParamsOrderBy.RATING.toLowerCase(),
        given: "false"
      }
    });
    $[0] = profileHandle;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = !!profileHandle;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: nicRatings
  } = useQuery(t4);
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const mq = globalThis.matchMedia("(min-width: 1024px)");
      const handler = e => {
        if (e.matches) {
          setIsGrantRepOpen(false);
          setIsNicRateOpen(false);
        }
      };
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    };
    t6 = [];
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  repRates?.rating_stats;
  let t7;
  if ($[9] !== repRates?.rating_stats) {
    t7 = repRates?.rating_stats ?? [];
    $[9] = repRates?.rating_stats;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = sortRepsByRatingAndContributors(t7);
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const reps = t8;
  let t9;
  if ($[13] !== activeProfileProxy || $[14] !== connectedProfile || $[15] !== profile) {
    t9 = getCanEditRep({
      myProfile: connectedProfile,
      targetProfile: profile,
      activeProfileProxy
    });
    $[13] = activeProfileProxy;
    $[14] = connectedProfile;
    $[15] = profile;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const canEditRep = t9;
  let t10;
  bb0: {
    if (!connectedProfile?.handle) {
      t10 = false;
      break bb0;
    }
    if (activeProfileProxy) {
      if (profile.handle === activeProfileProxy.created_by.handle) {
        t10 = false;
        break bb0;
      }
      let t11;
      if ($[17] !== activeProfileProxy.actions) {
        t11 = activeProfileProxy.actions.some(_temp);
        $[17] = activeProfileProxy.actions;
        $[18] = t11;
      } else {
        t11 = $[18];
      }
      t10 = t11;
      break bb0;
    }
    if (amIUser({
      profile,
      address
    })) {
      t10 = false;
      break bb0;
    }
    t10 = true;
  }
  const canEditNic = t10;
  let t11;
  if ($[19] !== activeProfileProxy || $[20] !== address || $[21] !== profile.handle || $[22] !== profile.wallets) {
    t11 = !activeProfileProxy && !!profile?.handle && (profile.wallets ?? []).some(w => w.wallet.toLowerCase() === address?.toLowerCase());
    $[19] = activeProfileProxy;
    $[20] = address;
    $[21] = profile.handle;
    $[22] = profile.wallets;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const canEditStatements = t11;
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setActiveTab("rep");
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = `tw-relative tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-p-4 tw-text-left tw-transition-all tw-duration-300 tw-ease-out ${activeTab === "rep" ? "tw-bg-[#0f1014]" : "tw-bg-white/[0.02]"}`;
  const t14 = `tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition-all tw-duration-300 ${activeTab === "rep" ? "tw-border-primary-300/90 tw-bg-transparent tw-ring-2 tw-ring-primary-500/25" : "tw-border-iron-400/45 tw-bg-black/10 tw-ring-1 tw-ring-black/20"}`;
  let t15;
  if ($[25] !== activeTab) {
    t15 = activeTab === "rep" && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-primary-300 tw-transition-all tw-duration-300" />;
    $[25] = activeTab;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t14 || $[28] !== t15) {
    t16 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-3 tw-z-10"><span className={t14}>{t15}</span></div>;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== activeTab) {
    t17 = activeTab === "rep" && <><div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-blue-500/[0.05] tw-via-transparent tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-iron-300/25 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-blue-400/40 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-blue-400/20 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-blue-400/20 tw-to-transparent" /></>;
    $[30] = activeTab;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const t18 = `tw-relative tw-transition-opacity tw-duration-300 ${activeTab === "rep" ? "" : "tw-opacity-40"}`;
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="tw-mb-1.5 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-leading-4 tw-tracking-wider tw-text-iron-500">Total Rep</div>;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== repRates) {
    t20 = repRates ? formatNumberWithCommas(repRates.total_rep_rating) : "\u2014";
    $[33] = repRates;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t20) {
    t21 = <div className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-text-primary-400">{t20}</div>;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== repRates) {
    t22 = repRates && <div className="tw-mt-2.5 tw-flex tw-items-center"><span className="tw-text-xs tw-font-normal tw-text-iron-400">{formatNumberWithCommas(repRates.number_of_raters)}{" "}{repRates.number_of_raters === 1 ? "rater" : "raters"}</span></div>;
    $[37] = repRates;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t18 || $[40] !== t21 || $[41] !== t22) {
    t23 = <div className={t18}>{t19}{t21}{t22}</div>;
    $[39] = t18;
    $[40] = t21;
    $[41] = t22;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== t13 || $[44] !== t16 || $[45] !== t17 || $[46] !== t23) {
    t24 = <button type="button" onClick={t12} className={t13}>{t16}{t17}{t23}</button>;
    $[43] = t13;
    $[44] = t16;
    $[45] = t17;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setActiveTab("identity");
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  const t26 = `tw-relative tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-p-4 tw-text-left tw-transition-all tw-duration-300 tw-ease-out ${activeTab === "identity" ? "tw-bg-[#0f1014]" : "tw-bg-white/[0.02]"}`;
  const t27 = `tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition-all tw-duration-300 ${activeTab === "identity" ? "tw-border-emerald-300/90 tw-bg-transparent tw-ring-2 tw-ring-emerald-500/25" : "tw-border-iron-400/45 tw-bg-black/10 tw-ring-1 tw-ring-black/20"}`;
  let t28;
  if ($[49] !== activeTab) {
    t28 = activeTab === "identity" && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-emerald-300 tw-transition-all tw-duration-300" />;
    $[49] = activeTab;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== t27 || $[52] !== t28) {
    t29 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-3 tw-z-10"><span className={t27}>{t28}</span></div>;
    $[51] = t27;
    $[52] = t28;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== activeTab) {
    t30 = activeTab === "identity" && <><div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-emerald-500/[0.05] tw-via-transparent tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-iron-300/25 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-emerald-400/40 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" /></>;
    $[54] = activeTab;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  const t31 = `tw-relative tw-transition-opacity tw-duration-300 ${activeTab === "identity" ? "" : "tw-opacity-40"}`;
  let t32;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <div className="tw-mb-1.5 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-leading-4 tw-tracking-wider tw-text-iron-500">NIC</div>;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  let t33;
  if ($[57] !== profile.cic) {
    t33 = formatNumberWithCommas(profile.cic);
    $[57] = profile.cic;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] !== t33) {
    t34 = <div className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-text-white">{t33}</div>;
    $[59] = t33;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== profile.cic) {
    t35 = <span className="tw-h-4 tw-w-4 tw-flex-shrink-0"><UserCICTypeIcon cic={profile.cic} /></span>;
    $[61] = profile.cic;
    $[62] = t35;
  } else {
    t35 = $[62];
  }
  let t36;
  if ($[63] !== profile.cic) {
    t36 = <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-emerald-400"><UserCICStatus cic={profile.cic} /></span>;
    $[63] = profile.cic;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] !== t35 || $[66] !== t36) {
    t37 = <div className="tw-mt-2.5 tw-flex tw-items-center tw-gap-1.5">{t35}{t36}</div>;
    $[65] = t35;
    $[66] = t36;
    $[67] = t37;
  } else {
    t37 = $[67];
  }
  const t38 = profile.handle ?? "";
  let t39;
  if ($[68] !== t38) {
    t39 = <div className="tw-pointer-events-none"><TopRaterAvatars handleOrWallet={t38} matter={RateMatter.NIC} count={3} size="sm" withLinks={false} /></div>;
    $[68] = t38;
    $[69] = t39;
  } else {
    t39 = $[69];
  }
  const t40 = nicRatings?.count ?? 0;
  let t41;
  if ($[70] !== t40) {
    t41 = formatNumberWithCommas(t40);
    $[70] = t40;
    $[71] = t41;
  } else {
    t41 = $[71];
  }
  const t42 = (nicRatings?.count ?? 0) === 1 ? "rater" : "raters";
  let t43;
  if ($[72] !== t41 || $[73] !== t42) {
    t43 = <span className="tw-text-xs tw-font-normal tw-text-iron-400">{t41}{" "}{t42}</span>;
    $[72] = t41;
    $[73] = t42;
    $[74] = t43;
  } else {
    t43 = $[74];
  }
  let t44;
  if ($[75] !== t39 || $[76] !== t43) {
    t44 = <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2">{t39}{t43}</div>;
    $[75] = t39;
    $[76] = t43;
    $[77] = t44;
  } else {
    t44 = $[77];
  }
  let t45;
  if ($[78] !== t31 || $[79] !== t34 || $[80] !== t37 || $[81] !== t44) {
    t45 = <div className={t31}>{t32}{t34}{t37}{t44}</div>;
    $[78] = t31;
    $[79] = t34;
    $[80] = t37;
    $[81] = t44;
    $[82] = t45;
  } else {
    t45 = $[82];
  }
  let t46;
  if ($[83] !== t26 || $[84] !== t29 || $[85] !== t30 || $[86] !== t45) {
    t46 = <button type="button" onClick={t25} className={t26}>{t29}{t30}{t45}</button>;
    $[83] = t26;
    $[84] = t29;
    $[85] = t30;
    $[86] = t45;
    $[87] = t46;
  } else {
    t46 = $[87];
  }
  let t47;
  if ($[88] !== t24 || $[89] !== t46) {
    t47 = <div className="tw-grid tw-grid-cols-2 tw-gap-3">{t24}{t46}</div>;
    $[88] = t24;
    $[89] = t46;
    $[90] = t47;
  } else {
    t47 = $[90];
  }
  let t48;
  if ($[91] !== activeTab || $[92] !== canEditNic || $[93] !== canEditRep || $[94] !== canEditStatements || $[95] !== initialActivityLogParams || $[96] !== profile || $[97] !== reps) {
    t48 = <AnimatePresence mode="wait">{activeTab === "rep" ? <motion.div key="rep" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease: "easeInOut"
      }}>{canEditRep && <div className="tw-mt-4"><UserPageRateWrapper profile={profile} type={RateMatter.REP} hideOwnProfileMessage={true}><div className="tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-solid tw-border-blue-500/20 tw-bg-blue-400/5 tw-px-5 tw-py-3"><span className="tw-text-xs tw-font-medium tw-text-blue-300/70">Add skill to this identity</span><button onClick={() => setIsGrantRepOpen(true)} className="tw-flex tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-primary-500 tw-bg-primary-500 tw-px-3.5 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-blue-500/20 tw-transition tw-duration-300 tw-ease-out hover:tw-border-primary-600 hover:tw-bg-primary-600 md:tw-py-3"><svg className="-tw-ml-1 tw-h-4 tw-w-4 tw-flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M12 5v14M5 12h14" /></svg>Grant Rep</button></div></UserPageRateWrapper></div>}{!!reps.length && <div className="tw-mt-4"><UserPageRepRepsTable reps={reps} profile={profile} canEditRep={canEditRep} initialCount={5} /></div>}<div className="tw-mt-6"><UserPageCombinedActivityLog initialActivityLogParams={initialActivityLogParams} matter={RateMatter.REP} withMatterFilter={false} /></div></motion.div> : <motion.div key="identity" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease: "easeInOut"
      }}>{canEditNic && <div className="tw-mt-4"><UserPageRateWrapper profile={profile} type={RateMatter.NIC} hideOwnProfileMessage={true}><div className="tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-solid tw-border-emerald-500/10 tw-bg-emerald-500/5 tw-px-5 tw-py-3"><span className="tw-text-xs tw-font-medium tw-text-emerald-200/70">Verify this identity</span><button onClick={() => setIsNicRateOpen(true)} className="tw-flex tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-emerald-500 tw-bg-emerald-500 tw-px-3.5 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-emerald-500/20 tw-transition tw-duration-300 tw-ease-out hover:tw-border-emerald-400 hover:tw-bg-emerald-400 sm:tw-py-3">Rate NIC</button></div></UserPageRateWrapper></div>}<div className="tw-mt-6 tw-flex tw-items-center tw-justify-between"><h3 className="tw-mb-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-iron-100">ID Statements</h3>{canEditStatements && <UserPageIdentityStatementsAddButton profile={profile} />}</div><div className="tw-mt-3 tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-bg-[#0f1014]"><UserPageIdentityStatements profile={profile} /></div><div className="tw-mt-6"><UserPageCombinedActivityLog initialActivityLogParams={initialActivityLogParams} matter={RateMatter.NIC} withMatterFilter={false} /></div></motion.div>}</AnimatePresence>;
    $[91] = activeTab;
    $[92] = canEditNic;
    $[93] = canEditRep;
    $[94] = canEditStatements;
    $[95] = initialActivityLogParams;
    $[96] = profile;
    $[97] = reps;
    $[98] = t48;
  } else {
    t48 = $[98];
  }
  let t49;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = () => setIsGrantRepOpen(false);
    $[99] = t49;
  } else {
    t49 = $[99];
  }
  let t50;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = () => setIsGrantRepOpen(false);
    $[100] = t50;
  } else {
    t50 = $[100];
  }
  let t51;
  if ($[101] !== profile || $[102] !== repRates) {
    t51 = <UserPageRepNewRep profile={profile} repRates={repRates} onSuccess={t50} />;
    $[101] = profile;
    $[102] = repRates;
    $[103] = t51;
  } else {
    t51 = $[103];
  }
  let t52;
  if ($[104] !== profile || $[105] !== t51) {
    t52 = <UserPageRateWrapper profile={profile} type={RateMatter.REP}>{t51}</UserPageRateWrapper>;
    $[104] = profile;
    $[105] = t51;
    $[106] = t52;
  } else {
    t52 = $[106];
  }
  let t53;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <div className="tw-mt-3"><button onClick={() => setIsGrantRepOpen(false)} type="button" className="tw-w-full tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-800">Cancel</button></div>;
    $[107] = t53;
  } else {
    t53 = $[107];
  }
  let t54;
  if ($[108] !== t52) {
    t54 = <div className="tw-px-4 sm:tw-px-6">{t52}{t53}</div>;
    $[108] = t52;
    $[109] = t54;
  } else {
    t54 = $[109];
  }
  let t55;
  if ($[110] !== isGrantRepOpen || $[111] !== t54) {
    t55 = <MobileWrapperDialog title="Grant Rep" isOpen={isGrantRepOpen} onClose={t49} tabletModal={true}>{t54}</MobileWrapperDialog>;
    $[110] = isGrantRepOpen;
    $[111] = t54;
    $[112] = t55;
  } else {
    t55 = $[112];
  }
  let t56;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = () => setIsNicRateOpen(false);
    $[113] = t56;
  } else {
    t56 = $[113];
  }
  let t57;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = () => setIsNicRateOpen(false);
    $[114] = t57;
  } else {
    t57 = $[114];
  }
  let t58;
  if ($[115] !== profile) {
    t58 = <UserPageIdentityHeaderCICRate profile={profile} isTooltip={false} onSuccess={t57} />;
    $[115] = profile;
    $[116] = t58;
  } else {
    t58 = $[116];
  }
  let t59;
  if ($[117] !== profile || $[118] !== t58) {
    t59 = <UserPageRateWrapper profile={profile} type={RateMatter.NIC}>{t58}</UserPageRateWrapper>;
    $[117] = profile;
    $[118] = t58;
    $[119] = t59;
  } else {
    t59 = $[119];
  }
  let t60;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <div className="tw-mt-3"><button onClick={() => setIsNicRateOpen(false)} type="button" className="tw-w-full tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-800">Cancel</button></div>;
    $[120] = t60;
  } else {
    t60 = $[120];
  }
  let t61;
  if ($[121] !== t59) {
    t61 = <div className="tw-px-4 sm:tw-px-6">{t59}{t60}</div>;
    $[121] = t59;
    $[122] = t61;
  } else {
    t61 = $[122];
  }
  let t62;
  if ($[123] !== isNicRateOpen || $[124] !== t61) {
    t62 = <MobileWrapperDialog title="Rate NIC" isOpen={isNicRateOpen} onClose={t56} tabletModal={true}>{t61}</MobileWrapperDialog>;
    $[123] = isNicRateOpen;
    $[124] = t61;
    $[125] = t62;
  } else {
    t62 = $[125];
  }
  let t63;
  if ($[126] !== t47 || $[127] !== t48 || $[128] !== t55 || $[129] !== t62) {
    t63 = <div className="lg:tw-hidden">{t47}{t48}{t55}{t62}</div>;
    $[126] = t47;
    $[127] = t48;
    $[128] = t55;
    $[129] = t62;
    $[130] = t63;
  } else {
    t63 = $[130];
  }
  return t63;
}
function _temp(action) {
  return action.action_type === ApiProfileProxyActionType.AllocateCic;
}